import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  DrawerBody,
  DrawerFooter,
  TextField,
  FormController,
  FormLabel,
  Button,
  ButtonGroup
} from '@one-thd/sui-atomic-components';
import { extend } from '@thd-nucleus/data-sources';
import { TRACK, ERROR, SCREENS } from '../../AddToConstants';
import { AddToListProductInfo } from './AddToListProductInfo';
import { useCreateList, createListModel } from '../../hooks/useLists';
import { AddToContext } from '../../AddToContext';

export const CreateList = ({
  onClose,
  onCreate,
  products,
  isExistingLists,
  isSharedList,
  setFavoriteCount,
  favoriteCount,
  productValue,
  identifiers,
  primaryImage,
  setNewListDetails
}) => {
  const { setScreen } = useContext(AddToContext);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);
  const { LIST_SUCCESS } = SCREENS;
  const isMultiAdd = products?.length > 1;
  const singleProduct = [
    { itemId: products[0]?.itemId, quantity: products[0]?.quantity }
  ];

  const multipleProduct = products.map((prod) => ({
    itemId: prod.itemId,
    quantity: prod.quantity
  }));

  const { createList, createListResponse } = useCreateList({
    products: isMultiAdd ? multipleProduct.reverse() : singleProduct
  });

  const getValidationError = (value) => {
    const validCharacters = /^[ [\]{}|a-z|A-Z|!@#$%^&*()0-9-_+,.?"':;\\]+$/;
    const isBlank = value.length === 0;
    let isValid = validCharacters.test(value);
    if (!isValid || isBlank) {
      if (isBlank) {
        setError(ERROR.EMPTY_LIST);
      } else {
        setError(ERROR.INVALID_LIST_NAME);
      }
    } else {
      setError(null);
    }
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (!error) {
      await createList({
        variables: {
          name
        }
      });
    }
  };

  useEffect(() => {
    if (!createListResponse.error && createListResponse?.data?.createList?.name) {
      let creationLocation = isExistingLists ? 'choose a list overlay' : 'create a list overlay';
      if (!isMultiAdd && products[0]?.itemId && !isSharedList) {
        if (window.THDCustomer?.default?.customerType === 'B2C') {
          creationLocation = 'save to your list overlay';
        }
        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.CREATE_LIST_WITH_ITEM, {
          creationLocation, quantity: products[0]?.quantity, price: productValue || 0, sku: products[0]?.itemId
        });
      } else if (products && isMultiAdd) {
        const listItem = products.map((prod) => ({
          listLocation: window?.digitalData?.page?.pageInfo?.pageType,
          quantity: prod.quantity || 'n/a',
          price: { basePrice: prod.price || 'n/a' },
          productInfo: { sku: prod.itemId || 'n/a' }
        }));
        if (isSharedList) {
          creationLocation = 'save shared list';
        }
        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.CREATE_LIST_WITH_PRODUCTS, {
          creationLocation, listItem
        });
      }
      onCreate();
    } else if (createListResponse?.error) {
      const gqlErrors = createListResponse?.error?.graphQLErrors;
      gqlErrors.forEach(function (err, index) {
        if (err?.message === ERROR.LIST_ALREADY_EXISTS) {
          setError(ERROR.LIST_ALREADY_EXISTS);
        } else {
          setError(ERROR.GENERAL_API_FAIL);
        }
      });
    }
  }, [createListResponse?.data, createListResponse?.error]);

  const newList = createListResponse?.data?.createList;

  useEffect(() => {
    if (newList?.id) {
      setFavoriteCount(favoriteCount + 1);
      setNewListDetails({
        listName: newList.name,
        listId: newList.id
      });
      setScreen(LIST_SUCCESS);
    }
  }, [newList?.id]);

  return (
    <>
      <DrawerBody>
        <AddToListProductInfo
          identifiers={identifiers}
          primaryImage={primaryImage}
        />
        <FormController className="sui-px-6 sui-pt-6">
          <FormLabel id="create-list" className="sui-cursor-default sui-text-base">
            Create List Name:
          </FormLabel>
          <div className="sui-pt-2">
            <TextField
              fullWidth
              placeholder="Enter a List Name"
              value={name}
              onChange={(evt) => {
                setName(evt.target.value);
                getValidationError(evt.target.value);
              }}
              status={error ? 'error' : null}
              statusMessage={error}
            />
          </div>
        </FormController>
      </DrawerBody>
      <DrawerFooter position="center">
        <div className="sui-flex sui-flex-col sui-gap-2">
          <ButtonGroup orientation="vertical">
            <Button
              data-testid="create-group-submit-button"
              fullWidth
              variant="primary"
              onClick={handleSubmit}
              type="submit"
              disabled={!name || !!error}
            >
              Save
            </Button>
            <Button
              data-testid="create-group-cancel-button"
              fullWidth
              variant="secondary"
              onClick={() => { onClose('close'); }}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </div>
      </DrawerFooter>
    </>
  );
};

CreateList.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  isExistingLists: PropTypes.bool.isRequired,
  isSharedList: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number
  })),
  setFavoriteCount: PropTypes.func.isRequired,
  favoriteCount: PropTypes.number,
  setNewListDetails: PropTypes.func,
  productValue: PropTypes.number,
  identifiers: PropTypes.shape({
    brandName: PropTypes.string,
    productLabel: PropTypes.string
  }),
  primaryImage: PropTypes.shape({
    url: PropTypes.string
  })
};

CreateList.defaultProps = {
  products: [],
  favoriteCount: null,
  productValue: 0,
  identifiers: {},
  primaryImage: {},
  setNewListDetails: () => { }
};

CreateList.displayName = 'CreateList';

CreateList.dataModel = extend(createListModel);